import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { useSubmitSupportRequest } from '../../api/useSubmitSupportRequest.mutation';
import {
  buildSupportRequestSchema,
  defaultSupportRequestFormValues,
  toSupportRequest,
  type SupportRequestFormValues,
} from '../../model/support-request.schema';
import { SupportRequestFields } from './SupportRequestFields';

export interface SupportRequestSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportRequestSheet({ open, onOpenChange }: SupportRequestSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Rows carry their own `px-xl`, so the form body can span the panel with its inset inside
          its clip. Gaps are one step short of what they read as: the body adds `2xs` of ring room
          above and below itself, and the success view's footer makes the same step up itself. */}
      <SheetContent className="w-[30rem] gap-md rounded-l-lg px-0 py-xl sm:max-w-none">
        <SupportRequestPanel onClose={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
}

interface SupportRequestPanelProps {
  onClose: () => void;
}

function SupportRequestPanel({ onClose }: SupportRequestPanelProps) {
  const { t } = useTranslation('common');
  const { t: tValidation } = useTranslation('validation');
  const schema = useMemo(() => buildSupportRequestSchema(tValidation), [tValidation]);
  const form = useForm<SupportRequestFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultSupportRequestFormValues,
    mode: 'onTouched',
  });
  const { mutate: submit, isPending, isSuccess, reset } = useSubmitSupportRequest();

  if (isSuccess) {
    return (
      <>
        <SheetHeader className="p-0 px-xl">
          <SheetTitle>{t('supportRequest.supportRequestSheet.requestSubmitted')}</SheetTitle>
          <SheetDescription>
            {t('supportRequest.supportRequestSheet.weveLoggedYourRequest')}
          </SheetDescription>
        </SheetHeader>
        <SheetFooter className="mt-2xs flex-row justify-end gap-xs p-0 px-xl">
          <Button
            variant="outline"
            onClick={() => {
              reset();
              form.reset();
            }}
          >
            {t('supportRequest.supportRequestSheet.submitAnother')}
          </Button>
          <Button onClick={onClose}>{t('supportRequest.supportRequestSheet.done')}</Button>
        </SheetFooter>
      </>
    );
  }

  return (
    <>
      <SheetHeader className="p-0 px-xl">
        <SheetTitle>{t('supportRequest.supportRequestSheet.support')}</SheetTitle>
        <SheetDescription>
          {t('supportRequest.supportRequestSheet.tellUsWhatYouNeed')}
        </SheetDescription>
      </SheetHeader>
      <Form {...form}>
        <form
          noValidate
          className="flex min-h-0 flex-1 flex-col gap-md"
          onSubmit={(event) => {
            void form.handleSubmit((values) => submit(toSupportRequest(values)))(event);
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto px-xl py-2xs">
            <SupportRequestFields control={form.control} disabled={isPending} />
          </div>
          <SheetFooter className="flex-row justify-end gap-xs p-0 px-xl">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
              {t('supportRequest.supportRequestSheet.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Spinner />}
              {t('supportRequest.supportRequestSheet.sendRequest')}
            </Button>
          </SheetFooter>
        </form>
      </Form>
    </>
  );
}
